
/**
 * mwApp.packages module <br>
 * Work with packages (networks): show/add/edit/remove
 *
 * @namespace mwApp/packages
 */

/**
 * ConfigRoutes
 * Configurations module routes
 *
 * @class ConfigRoutes
 * @memberof mwApp/packages
 * @see mwApp/packages
 *
 * @ngdoc config
 * @name mwApp/packages.config:ConfigRoutes
 */
/* @ngInject */

export default function ConfigRoutes ($stateProvider) {
  $stateProvider

    /**
     * abstract state for /packages
     */
    .state('app.organizations', {
      abstract: true,
      url: '/organizations',
      template: '<ui-view/>',
      resolve: {
        inapp: /* @ngInject */ function (InappSettings) {
          return InappSettings.lazyLoad()
            .catch(() => ({})) // suppress error in case of failed loading
        }
      }
    })

    /**
     * controller {@link mwApp/packages.controller:PackagesCtrl}
     * @name /organizations
     * @memberof mwApp/packages.config:ConfigRoutes
     */
    .state('app.organizations.list', {
      url: '',
      templateUrl: 'components/packages/views/packages.html',
      controller: 'PackagesCtrl as vm',
      label: 'Organizations',
      access: 'viewNetwork'
    })

    /**
     * abstract state for /organizations/:packageId
     */
    .state('app.organizations.organization', {
      abstract: true,
      url: '/:packageId',
      template: '<ui-view/>',
      resolve: {
        pckg: /* @ngInject */ function ($transition$, Network) {
          var { packageId: id } = $transition$.params()
          return +id
            ? Network.load({ id })
            : new Network()
        },
        ecwid: /* @ngInject */ function ($transition$, Ecwid, authSvc) {
          var { packageId: networkId } = $transition$.params()
          if (!authSvc.hasEnabledFlag('ECOMMERCE_PROVIDER_ENABLED')) {
            return null
          }
          return +networkId
            ? Ecwid.load({ networkId })
            : new Ecwid()
        },
        apiAccess: /* @ngInject */ function ($transition$, Network, authSvc) {
          var { packageId: networkId } = $transition$.params()
          return (+networkId && authSvc.hasAccess('viewAPIAccess')) ? Network.getApiAccess(networkId).then(res => res.data) : null
        },
        generalSettings: /* @ngInject */ function (Setting) {
          return Setting.loadAll()
        },
        templateVariables: /* @ngInject */ function ($transition$, TemplateVariable) {
          const { packageId: networkId } = $transition$.params()
          return +networkId ? TemplateVariable.getTemplatesByEntity(networkId) : []
        }
      }
    })

    /**
     * controller {@link mwApp/packages.controller:AddPackageCtrl}
     * @name /organizations/:packageId
     * @memberof mwApp/packages.config:ConfigRoutes
     */
    .state('app.organizations.organization.edit', {
      url: '',
      templateUrl: 'components/packages/views/addPackage.html',
      controller: 'AddPackageCtrl as vm',
      label: '{{::pckg.name || ((packageId | addOrEdit) + " Organization")}}',
      access: ':packageId (+) ! IF createNetwork ELSE editNetwork'
    })

    /**
     * abstract state for /monetization-plans
     */
    .state('app.organizations.organization.monetizationPlans', {
      abstract: true,
      url: '/monetization-plans',
      template: '<ui-view/>',
      resolve: {
        networkCurrencies: /* @ngInject */ function (NetworkCurrency, pckg, authSvc) {
          return !(+pckg.id && authSvc.hasAccess('viewNetworkCurrency'))
            ? []
            : NetworkCurrency.loadAll({ network: pckg }, false).then(data => data.currenciesEnabled)
        }
      }
    })

    /**
     * controller {@link mwApp/packages.controller:SubscriptionSettingsCtrl}
     * @name /organizations/:packageId/monetization-plans
     * @memberof mwApp/packages.config:ConfigRoutes
     */
    .state('app.organizations.organization.monetizationPlans.list', {
      url: '',
      label: 'Monetization Plans',
      access: 'editNetwork viewSubscription &',
      views: {
        '': {
          templateUrl: 'components/packages/views/subscriptionSettings.html',
          controller: 'SubscriptionSettingsCtrl as vm'
        },
        'monetizationTypeAccessories@app.organizations.organization.monetizationPlans.list': {
          templateUrl: 'components/packages/views/monetizationTypeAccessories.html',
          controller: 'MonetizationTypeAccessoriesCtrl as vm'
        }
      }
    })

    /**
     * abstract state for /accessories
     */
    .state('app.organizations.organization.monetizationPlans.accessories', {
      abstract: true,
      url: '/accessories',
      template: '<ui-view/>',
      resolve: {}
    })

    /**
     * abstract state for /accessories/:accessoryId
     */
    .state('app.organizations.organization.monetizationPlans.accessories.accessory', {
      abstract: true,
      url: '/:accessoryId',
      template: '<ui-view/>',
      resolve: {
        accessory: /* @ngInject */ function ($transition$, SubscriptionAccessory, pckg) {
          const { accessoryId: id } = $transition$.params()
          return +id ? SubscriptionAccessory.load({ networkId: pckg.id, id }) : new SubscriptionAccessory({ networkId: pckg.id })
        }
      }
    })

    /**
     * controller {@link mwApp/packages.controller:AddSubscriptionSettingsCtrl}
     * @name /addAccessory
     * @memberof mwApp/packages.config:ConfigRoutes
     */
    .state('app.organizations.organization.monetizationPlans.accessories.accessory.edit', {
      url: '',
      templateUrl: 'components/packages/views/addAccessory.html',
      controller: 'AddAccessoryCtrl as vm',
      label: '{{::accessoryId | addOrEdit}} Accessory',
      access: ':accessoryId (+) ! IF createSubscription ELSE editSubscription'
    })

    /**
     * abstract state for /subscriptions
     */
    .state('app.organizations.organization.monetizationPlans.subscriptions', {
      abstract: true,
      url: '/subscriptions',
      template: '<ui-view/>',
      resolve: {}
    })

    /**
     * abstract state for /monetization-plans/subscriptions/:subscriptionId
     */
    .state('app.organizations.organization.monetizationPlans.subscriptions.subscription', {
      abstract: true,
      url: '/:subscriptionId',
      template: '<ui-view/>',
      resolve: {
        subscription: /* @ngInject */ function ($transition$, Subscription, pckg) { // pckg resolved in parent state above
          var { subscriptionId: id } = $transition$.params()
          return +id
            ? (Subscription.load({ networkId: pckg.id, id }))
            : (new Subscription({ networkId: pckg.id, amounts: [{ name: 'Price' }] }))
        },
        networkSettings: /* @ngInject */ function (NetworkSetting, pckg) {
          return NetworkSetting.loadAll({ network: pckg })
        },
        scope: function () {
          // use object 'scope' to share object between different views
          return {}
        }
      }
    })

    /**
     * controller {@link mwApp/packages.controller:AddSubscriptionSettingsCtrl}
     * @name /addsubscription
     * @memberof mwApp/packages.config:ConfigRoutes
     */
    .state('app.organizations.organization.monetizationPlans.subscriptions.subscription.edit', {
      url: '',
      views: {
        '': {
          templateUrl: 'components/packages/views/addSubscriptionSettings.html',
          controller: 'AddSubscriptionSettingsCtrl as vm'
        },
        'subscriptionContentSets@app.organizations.organization.monetizationPlans.subscriptions.subscription.edit': {
          templateUrl: 'components/packages/views/subscriptionContentSets.html',
          controller: 'SubscriptionContentSetsCtrl as vm'
        },
        'subscriptionAccessories@app.organizations.organization.monetizationPlans.subscriptions.subscription.edit': {
          templateUrl: 'components/packages/views/subscriptionAccessories.html',
          controller: 'SubscriptionAccessoriesCtrl as vm'
        },
        'subscriptionDiscountSettings@app.organizations.organization.monetizationPlans.subscriptions.subscription.edit': {
          templateUrl: 'components/packages/views/subscriptionDiscountSettings.html',
          controller: 'SubscriptionDiscountSettingsCtrl as vm'
        },
        'subscriptionPartnerProducts@app.organizations.organization.monetizationPlans.subscriptions.subscription.edit': {
          templateUrl: 'components/packages/views/subscriptionPartnerProducts.html',
          controller: 'SubscriptionPartnerProductsCtrl as vm'
        }
      },
      label: '{{::subscriptionId | addOrEdit}} Subscription',
      access: ':subscriptionId (+) ! IF createSubscription ELSE editSubscription'
    })

    /**
     * controller {@link mwApp/packages.controller:SubscriptionSettingsCtrl}
     * @name /organizations/:packageId/customizeClientApp/channelListing/:subscriptionId
     * @memberof mwApp/packages.config:ConfigRoutes
     */
    .state('app.organizations.organization.customizeClientApp.channelListing', {
      url: '/channelListing/:subscriptionId',
      templateUrl: 'components/packages/views/subscriptionChannelListing.html',
      controller: 'SubscriptionChannelListingCtrl as vm',
      label: 'TV Channel Listing',
      resolve: {
        type: function () {
          return 'tv'
        },
        subscription: /* @ngInject */ function ($transition$, Subscription, pckg) {
          const { subscriptionId: id } = $transition$.params()
          return Subscription.load({ networkId: pckg.id, id })
        },
        cl: /* @ngInject */ function ($transition$, SubscriptionChannelListing, pckg, type) { // pckg resolved in parent state above
          const { subscriptionId } = $transition$.params()
          return SubscriptionChannelListing.load({
            networkId: pckg.id,
            subscriptionId,
            type
          }).then((cl) => { // TODO: need info from BE!
            cl.networkId = pckg.id
            cl.subscriptionId = subscriptionId
            cl.type = type
            return cl
          })
        },
        ungroupedChannels: /* @ngInject */ function ($transition$, SubscriptionChannelListing, pckg, type) { // pckg resolved in parent state above
          const { subscriptionId } = $transition$.params()
          return SubscriptionChannelListing.getUngroupedChannels({
            networkId: pckg.id,
            subscriptionId,
            type
          }).then((channels) => { // TODO: need info from BE!
            return channels
          })
        }
      }
    })

    /**
     * controller {@link mwApp/packages.controller:SubscriptionSettingsCtrl}
     * @name /organizations/:packageId/customizeClientApp/radioChannelListing/:subscriptionId
     * @memberof mwApp/packages.config:ConfigRoutes
     */
    .state('app.organizations.organization.customizeClientApp.radioChannelListing', {
      url: '/radioChannelListing/:subscriptionId',
      templateUrl: 'components/packages/views/subscriptionChannelListing.html',
      controller: 'SubscriptionChannelListingCtrl as vm',
      label: 'Radio Channel Listing',
      resolve: {
        type: function () {
          return 'radio'
        },
        subscription: /* @ngInject */ function ($transition$, Subscription, pckg) {
          const { subscriptionId: id } = $transition$.params()
          return Subscription.load({ networkId: pckg.id, id })
        },
        cl: /* @ngInject */ function ($transition$, SubscriptionChannelListing, pckg, type) { // pckg resolved in parent state above
          const { subscriptionId } = $transition$.params()
          return SubscriptionChannelListing.load({
            networkId: pckg.id,
            subscriptionId,
            type
          }).then((cl) => { // TODO: need info from BE!
            cl.networkId = pckg.id
            cl.subscriptionId = subscriptionId
            cl.type = type
            return cl
          })
        },
        ungroupedChannels: /* @ngInject */ function ($transition$, SubscriptionChannelListing, pckg, type) { // pckg resolved in parent state above
          const { subscriptionId } = $transition$.params()
          return SubscriptionChannelListing.getUngroupedChannels({
            networkId: pckg.id,
            subscriptionId,
            type
          }).then((channels) => { // TODO: need info from BE!
            return channels
          })
        }
      }
    })

    /**
     * controller {@link mwApp/packages.controller:AnnouncementsCtrl}
     * @name /organizations/:packageId/announcements
     * @memberof mwApp/packages.config:ConfigRoutes
     */
    .state('app.organizations.organization.announcements', {
      url: '/announcements',
      templateUrl: 'components/packages/views/announcements.html',
      controller: 'AnnouncementsCtrl as vm',
      label: 'Announcements and Reminders',
      access: 'editNetwork viewAnnouncement &'
    })

  // /**
  //  * controller {@link mwApp/packages.controller:DiscountsCtrl}
  //  * @name /organizations/:packageId/discounts
  //  * @memberof mwApp/packages.config:ConfigRoutes
  //  */
  // .state('app.organizations.organization.discounts', {
  //   url: '/discounts',
  //   templateUrl: 'components/packages/views/discounts.html',
  //   controller: 'DiscountsCtrl as vm',
  //   label: 'Discount & Coupons',
  //   access: '' // TODO!
  // })

    /**
     * abstract state for /contentSets
     */
    .state('app.organizations.organization.contentSets', {
      abstract: true,
      url: '/contentSets',
      template: '<ui-view/>'
    })

    /**
     * controller {@link mwApp/packages.controller:ContentSetsCtrl}
     * @name /organizations/:packageId/contentSets
     * @memberof mwApp/packages.config:ConfigRoutes
     */
    .state('app.organizations.organization.contentSets.list', {
      url: '',
      views: {
        '': {
          templateUrl: 'components/packages/views/contentSets.html',
          controller: 'ContentSetsCtrl as vm'
        }
      },
      label: 'Content Sets',
      access: '' // TODO!
    })

    /**
     * controller {@link mwApp/packages.controller:AddVodSetCtrl}
     * @name /organizations/:packageId/contentSets/vod/:vodSetId
     * @memberof mwApp/packages.config:ConfigRoutes
     */
    .state('app.organizations.organization.contentSets.vod', {
      url: '/vod/:vodSetId',
      views: {
        '': {
          templateUrl: 'components/packages/views/addVodSet.html',
          controller: 'AddVodSetCtrl as vm'
        },
        'shoppingCartSettings@app.organizations.organization.contentSets.vod': {
          templateUrl: 'components/packages/views/shoppingCartSettings.html',
          controller: 'ShoppingCartSettingsCtrl as vm'
        }
      },
      label: '{{::vodSetId | addOrEdit}} VOD Set',
      resolve: {
        cs: /* @ngInject */ function ($transition$, VODSet, pckg) { // pckg resolved in parent state above
          var { vodSetId: id } = $transition$.params()
          return +id
            ? (
              VODSet.load({
                networkId: pckg.id,
                id
              }).then((vs) => {
                vs.networkId = pckg.id
                return vs
              })
            )
            : (
              new VODSet({
                networkId: pckg.id
              })
            )
        }
      }
    })

    /**
     * controller {@link mwApp/packages.controller:AddShowSetCtrl}
     * @name /organizations/:packageId/contentSets/show/:showSetId
     * @memberof mwApp/packages.config:ConfigRoutes
     */
    .state('app.organizations.organization.contentSets.show', {
      url: '/show/:showSetId',
      views: {
        '': {
          templateUrl: 'components/packages/views/addShowSet.html',
          controller: 'AddShowSetCtrl as vm'
        },
        'shoppingCartSettings@app.organizations.organization.contentSets.show': {
          templateUrl: 'components/packages/views/shoppingCartSettings.html',
          controller: 'ShoppingCartSettingsCtrl as vm'
        }
      },
      label: '{{::showSetId | addOrEdit}} Show Set',
      resolve: {
        cs: /* @ngInject */ function ($transition$, ShowSet, pckg) { // pckg resolved in parent state above
          var { showSetId: id } = $transition$.params()
          return +id
            ? (
              ShowSet.load({
                networkId: pckg.id,
                id
              }).then((ss) => {
                ss.networkId = pckg.id
                return ss
              })
            )
            : (
              new ShowSet({
                networkId: pckg.id
              })
            )
        }
      }
    })

    /**
     * controller {@link mwApp/packages.controller:AddChannelSetCtrl}
     * @name /organizations/:packageId/contentSets/channel/:channelSetId
     * @memberof mwApp/packages.config:ConfigRoutes
     */
    .state('app.organizations.organization.contentSets.channel', {
      url: '/channel/:channelSetId',
      views: {
        '': {
          templateUrl: 'components/packages/views/addChannelSet.html',
          controller: 'AddChannelSetCtrl as vm'
        },
        'shoppingCartSettings@app.organizations.organization.contentSets.channel': {
          templateUrl: 'components/packages/views/shoppingCartSettings.html',
          controller: 'ShoppingCartSettingsCtrl as vm'
        }
      },
      label: '{{::channelSetId | addOrEdit}} Channel Set',
      resolve: {
        cs: /* @ngInject */ function ($transition$, ChannelSet, pckg) { // pckg resolved in parent state above
          var { channelSetId: id } = $transition$.params()
          return +id
            ? (
              ChannelSet.load({
                networkId: pckg.id,
                id
              }).then((cs) => { // TODO: need info from BE!
                cs.networkId = pckg.id
                return cs
              })
            )
            : (
              new ChannelSet({
                networkId: pckg.id
              })
            )
        }
      }
    })

    /**
     * controller {@link mwApp/packages.controller:AddLiveEventSetCtrl}
     * @name /organizations/:packageId/contentSets/liveEvent/:liveEventSetId
     * @memberof mwApp/packages.config:ConfigRoutes
     */
    .state('app.organizations.organization.contentSets.liveEvent', {
      url: '/liveEvent/:liveEventSetId',
      views: {
        '': {
          templateUrl: 'components/packages/views/addLiveEventSet.html',
          controller: 'AddLiveEventSetCtrl as vm'
        },
        'shoppingCartSettings@app.organizations.organization.contentSets.liveEvent': {
          templateUrl: 'components/packages/views/shoppingCartSettings.html',
          controller: 'ShoppingCartSettingsCtrl as vm'
        }
      },
      label: '{{::liveEventSetId | addOrEdit}} Live Event Set',
      resolve: {
        cs: /* @ngInject */ function ($transition$, LiveEventSet, pckg) { // pckg resolved in parent state above
          var { liveEventSetId: id } = $transition$.params()
          return +id
            ? (
              LiveEventSet.load({
                networkId: pckg.id,
                id
              }).then((les) => { // TODO: need info from BE!
                les.networkId = pckg.id
                return les
              })
            )
            : (
              new LiveEventSet({
                networkId: pckg.id
              })
            )
        }
      }
    })

    /**
     * controller {@link mwApp/packages.controller:AddMultiSetCtrl}
     * @name /organizations/:packageId/contentSets/multi/:multiSetId
     * @memberof mwApp/packages.config:ConfigRoutes
     */
    .state('app.organizations.organization.contentSets.multi', {
      url: '/multi/:multiSetId',
      views: {
        '': {
          templateUrl: 'components/packages/views/addMultiSet.html',
          controller: 'AddMultiSetCtrl as vm'
        },
        'shoppingCartSettings@app.organizations.organization.contentSets.multi>': {
          templateUrl: 'components/packages/views/shoppingCartSettings.html',
          controller: 'ShoppingCartSettingsCtrl as vm'
        }
      },
      label: '{{::multiSetId | addOrEdit}} Multi Set',
      resolve: {
        ms: /* @ngInject */ function ($transition$, MultiSet, pckg) { // pckg resolved in parent state above
          var { multiSetId: id } = $transition$.params()
          return +id
            ? (
              MultiSet.load({
                networkId: pckg.id,
                id
              }).then((ms) => { // TODO: need info from BE!
                ms.networkId = pckg.id
                return ms
              })
            )
            : (
              new MultiSet({
                networkId: pckg.id
              })
            )
        }
      }
    })

    /**
     * abstract state for /customization
     */
    .state('app.organizations.organization.customizeClientApp', {
      abstract: true,
      url: '/customization',
      template: '<ui-view/>'
    })

    /**
     * controller {@link mwApp/packages.controller:CustomizeClientAppCtrl}
     * @name /organizations/:packageId/customization
     * @memberof mwApp/packages.config:ConfigRoutes
     */
    .state('app.organizations.organization.customizeClientApp.list', {
      url: '',
      resolve: {
        scope: function () {
          // use object 'scope' to share object between different views
          return {}
        },
        subscriptions: /* @ngInject */ function ($transition$, Subscription) {
          const { packageId: networkId } = $transition$.params()
          return Subscription.loadAll({ networkId })
            .then(data => data.filter(s => s.subscriptionType === 'Subscription'))
        },
        cts: /* @ngInject */ function ($transition$, Banner, authSvc) {
          const { packageId: networkId } = $transition$.params()
          if (authSvc.hasAccess('editFeaturedCarousel')) {
            return Banner.getContentTypes(networkId).then(data => data)
          }
          return {}
        },
        bannerSubscriptions: /* @ngInject */ function ($transition$, Banner) {
          const { packageId: networkId } = $transition$.params()
          return Banner.getAvailableSubscriptions(networkId)
        },
        featuredSubscriptions: /* @ngInject */ function ($transition$, Featured) {
          const { packageId: networkId } = $transition$.params()
          return Featured.getAvailableSubscriptions(networkId)
        },
        motn: /* @ngInject */ function ($transition$, MovieOfTheNight) {
          const { packageId: networkId } = $transition$.params()
          return MovieOfTheNight.load({ networkId }).then(res => {
            res.networkId = networkId
            return res
          })
        },
        hints: /* @ngInject */ function ($http, pckg) {
          if (!pckg || !pckg.id) return []
          return $http.get(`api/networks/${pckg.id}/search-hint`)
            .then(res => res.data.payload || [])
            .catch(() => [])
        }
        // scope: /* @ngInject */ function () {
        //   return {}
        // }
        // shelves: /* @ngInject */ function ($transition$, Shelf) {
        //   const { packageId: networkId } = $transition$.params()
        //   return Shelf.getAvailableSubscriptions(networkId)
        // }

      },
      views: {
        '': {
          templateUrl: 'components/packages/views/customizeClientApp.html',
          controller: 'CustomizeClientAppsCtrl as vm'
        },
        'promotion@app.organizations.organization.customizeClientApp.list': {
          templateUrl: 'components/packages/views/promotion.html',
          controller: 'PromotionCtrl as vm'
        },
        'promotion_preview@app.organizations.organization.customizeClientApp.list': {
          templateUrl: 'components/packages/appCustomization/Preview/preview.html',
          controller: 'PreviewCtrl as vm'
        },
        'moduleConfiguration@app.organizations.organization.customizeClientApp.list': {
          templateUrl: 'components/packages/views/moduleConfiguration.html',
          controller: 'ModuleConfigurationCtrl as vm'
        },
        'menu_preview@app.organizations.organization.customizeClientApp.list': {
          templateUrl: 'components/packages/appCustomization/MenuPreview/menuPreview.html',
          controller: 'MenuPreviewCtrl as vm'
        },
        'channelListing@app.organizations.organization.customizeClientApp.list': {
          templateUrl: 'components/packages/views/channelListing.html',
          controller: 'ChannelListingCtrl as vm'
        },
        'general@app.organizations.organization.customizeClientApp.list': {
          templateUrl: 'components/packages/views/packageGeneralSettings.html',
          controller: 'PackageGeneralSettingsCtrl as vm'
        },
        'settings@app.organizations.organization.customizeClientApp.list': {
          templateUrl: 'components/packages/views/settings.html',
          controller: 'SettingsCtrl as vm'
        },
        'shelves@app.organizations.organization.customizeClientApp.list': {
          templateUrl: 'components/packages/components/shelves/editShelves.html',
          controller: 'EditShelvesCtrl as vm'
        },
        'aiHints@app.organizations.organization.customizeClientApp.list': {
          templateUrl: 'components/packages/components/aiHints/editAiHints.html',
          controller: 'EditAiHintsCtrl as vm'
        },
        'preview_hints@app.organizations.organization.customizeClientApp.list': {
          templateUrl: 'components/packages/appCustomization/Preview/preview_hints.html',
          controller: 'PreviewCtrl as vm'
        }
      },
      label: 'Apps Customization',
      access: 'viewBannerCarousel viewFeaturedCarousel | editNetwork &'
    })

    /**
     * abstract state for /modules
     */
    .state('app.organizations.organization.customizeClientApp.modules', {
      abstract: true,
      url: '/modules',
      template: '<ui-view/>',
      resolve: {}
    })

    /**
     * abstract state for /modules/:moduleId
     */
    .state('app.organizations.organization.customizeClientApp.modules.module', {
      abstract: true,
      url: '/:moduleId',
      template: '<ui-view/>'
    })

    /**
     * controller {@link mwApp/packages.controller:AddModuleSettingsCtrl}
     * @name /addModule
     * @memberof mwApp/packages.config:ConfigRoutes
     */
    .state('app.organizations.organization.customizeClientApp.modules.module.edit', {
      url: '?moduleType',
      label: '{{::moduleId | addOrEdit}} Module',
      access: 'editNetwork',
      resolve: {
        module: /* @ngInject */ function ($transition$, ModuleSettings, pckg) {
          const { moduleId: id, moduleType } = $transition$.params()
          const model = { networkId: pckg.id }
          if (moduleType) { model.moduleType = moduleType }
          return +id ? ModuleSettings.load({ ...model, id }).then((res) => new ModuleSettings({ ...res, ...model })) : new ModuleSettings(model)
        },
        scope: function () {
          // use object 'scope' to share object between different views
          return {}
        },
        assignedItems: /* @ngInject */ function ($transition$, ModuleSettings, pckg) {
          const params = { moduleTypes: 'PREDEFINED,KIDS,CUSTOM' }

          return ModuleSettings.loadAll({ networkId: pckg.id, params })
            .then(allModules => {
              const items = { title: [], identifier: [], iconName: [] }

              for (const m of allModules) {
                items.identifier.push(m.identifier)

                if (['PREDEFINED', 'CUSTOM'].includes(m.moduleType)) {
                  items.title.push(m.title)
                  items.iconName.push(m.iconName)
                }
              }
              return items
            })
        }
      },
      views: {
        '': {
          templateUrl: 'components/packages/views/addModuleSettings.html',
          controller: 'AddModuleSettingsCtrl as vm'
        },
        'promotion_preview@app.organizations.organization.customizeClientApp.modules.module.edit': {
          templateUrl: 'components/packages/appCustomization/Preview/preview.html',
          controller: 'PreviewCtrl as vm'
        }
      }
    })

    /**
     * abstract state for /shoppingCart
     */
    .state('app.organizations.organization.shoppingCart', {
      abstract: true,
      url: '/shoppingCart',
      template: '<ui-view/>'
    })

    /**
     * controller {@link mwApp/packages.controller:ShoppingCartCtrl}
     * @name /organizations/:packageId/shoppingCart
     * @memberof mwApp/packages.config:ConfigRoutes
     */
    .state('app.organizations.organization.shoppingCart.list', {
      url: '',
      views: {
        '': {
          templateUrl: 'components/packages/views/shoppingCart.html',
          controller: 'ShoppingCartCtrl as vm'
        }
      },
      label: 'Shopping Cart',
      access: 'editNetwork viewShoppingCart &',
      resolve: {
        enabled: /* @ngInject */ function ($state, authSvc) {
          if (!authSvc.hasEnabledFlag('FEATURE_SHOPPING_CART_MANAGEMENT_ENABLED')) {
            $state.go('app.403')
          }
        }
      }
    })

    /**
     * abstract state for /appLocalization
     */
    .state('app.organizations.organization.appLocalization', {
      abstract: true,
      url: '/appLocalization',
      template: '<ui-view/>',
      resolve: {
        scope: /* @ngInject */ function ($state, authSvc) {
          if (!authSvc.hasEnabledFlag('FEATURE_APP_LOCALIZATION_ENABLED')) {
            $state.go('app.404')
          }
          // use object 'scope' to share object between different views
          return {
            featureEnabled: true
          }
        }
      }
    })

    /**
     * controller {@link mwApp/localizations.controller:OrganizationLocalizationsCtrl}
     * @name /organizations/:packageId/appLocalization
     * @memberof mwApp/packages.config:ConfigRoutes
     */
    .state('app.organizations.organization.appLocalization.list', {
      url: '',
      templateUrl: 'components/packages/views/organizationLocalizations.html',
      controller: 'OrganizationLocalizationsCtrl as vm',
      label: 'App Localization',
      access: 'viewLocalization',
      resolve: {
        pckg: /* @ngInject */ function ($transition$, Network) {
          var { packageId: id } = $transition$.params()
          return +id ? Network.load({ id }) : null
        },
        inapp: /* @ngInject */ function (InappSettings) {
          return InappSettings.lazyLoad()
            .catch(() => ({})) // suppress error in case of failed loading
        }
      }
    })

    /**
     * intermediate state for /appLocalization/client (for breadcrumbs)
     */
    .state('app.organizations.organization.appLocalization.client', {
      url: '/clients',
      label: 'Client App',
      access: '',
      redirectTo: function (trans) {
        const $state = trans.router.stateService
        $state.restoreHash = 'clients'
        return { state: 'app.organizations.organization.appLocalization.list' }
      }
    })

    /**
     * state for /appLocalization/client/:localizationId
     */
    .state('app.organizations.organization.appLocalization.client.detail', {
      url: '/:localizationId',
      templateUrl: 'components/localization/views/addLocalization.html',
      controller: 'AddLocalizationCtrl as vm',
      label: '{{::localizationId | addOrEdit}} Localization',
      access: '',
      resolve: {
        localization: /* @ngInject */ function ($transition$, Localization, pckg) {
          const { localizationId: locale } = $transition$.params()
          const appName = 'client-apps'
          if (locale !== 'new') {
            return Localization.loadLocale({ localization: { locale: { tag: locale } }, appName, networkId: pckg?.id })
              .then(loc => {
                if (loc && pckg?.id) {
                  loc.networkId = pckg.id
                }
                return loc
              })
          } else {
            const newLoc = new Localization({ appName, localization: { dictionary: {}, locale: null } })
            if (pckg?.id) {
              newLoc.networkId = pckg.id
            }
            return newLoc
          }
        },
        localizations: /* @ngInject */ function (Localization, pckg) {
          return Localization.loadAll({ appName: 'client-apps', networkId: pckg?.id })
            .then(data => { return data })
        },
        glossary: /* @ngInject */ function (Localization) {
          return Localization.getGlossary('client-apps')
        }
      }
    })

    /**
     * intermediate state for /appLocalization/application (for breadcrumbs)
     */
    .state('app.organizations.organization.appLocalization.application', {
      url: '/applications',
      label: 'Shopping Cart',
      access: '',
      redirectTo: function (trans) {
        const $state = trans.router.stateService
        $state.restoreHash = 'applications'
        return { state: 'app.organizations.organization.appLocalization.list' }
      }
    })

    /**
     * state for /appLocalization/application/:localizationId
     */
    .state('app.organizations.organization.appLocalization.application.detail', {
      url: '/:localizationId',
      templateUrl: 'components/localization/views/addLocalization.html',
      controller: 'AddLocalizationCtrl as vm',
      label: '{{::localizationId | addOrEdit}} Localization',
      access: '',
      resolve: {
        enabled: /* @ngInject */ function ($state, authSvc) {
          if (!authSvc.hasEnabledFlag('FEATURE_SHOPPING_CART_MANAGEMENT_ENABLED')) {
            $state.go('app.403')
          }
        },
        localization: /* @ngInject */ function ($transition$, Localization, pckg) {
          const { localizationId: locale } = $transition$.params()
          const appName = 'scp'
          if (locale !== 'new') {
            return Localization.loadLocale({ localization: { locale: { tag: locale } }, appName, networkId: pckg?.id })
              .then(loc => {
                if (loc && pckg?.id) {
                  loc.networkId = pckg.id
                }
                return loc
              })
          } else {
            const newLoc = new Localization({ appName, localization: { dictionary: {}, locale: null } })
            if (pckg?.id) {
              newLoc.networkId = pckg.id
            }
            return newLoc
          }
        },
        localizations: /* @ngInject */ function (Localization, pckg) {
          return Localization.loadAll({ appName: 'scp', networkId: pckg?.id })
            .then(data => { return data })
        },
        glossary: /* @ngInject */ function (Localization) {
          return Localization.getGlossary('scp')
        }
      }
    })

    /**
     * abstract state for /paymentServices
     */
    .state('app.organizations.organization.paymentServices', {
      abstract: true,
      url: '/paymentServices',
      template: '<ui-view/>'
    })

    /**
     * controller {@link mwApp/packages.controller:PaymentServicesCtrl}
     * @name /organizations/:packageId/paymentServices
     * @memberof mwApp/packages.config:ConfigRoutes
     */
    .state('app.organizations.organization.paymentServices.list', {
      url: '',
      templateUrl: 'components/packages/views/paymentServices.html',
      controller: 'PaymentServicesCtrl as vm',
      label: 'Payment Services',
      access: 'viewNetworkPayment',
      resolve: {
        generalSettings: /* @ngInject */ function (Setting) {
          return Setting.loadAll()
        }
      }
    })

    /**
     * abstract state for /partner-products
     */
    .state('app.organizations.organization.partnerProducts', {
      abstract: true,
      url: '/partner-products',
      template: '<ui-view/>'
    })

    /**
     * controller {@link mwApp/packages.controller:PartnerProductsCtrl}
     * @name /organizations/:packageId/partner-products
     * @memberof mwApp/packages.config:ConfigRoutes
     */
    .state('app.organizations.organization.partnerProducts.list', {
      url: '',
      views: {
        '': {
          templateUrl: 'components/packages/views/partnerProducts.html',
          controller: 'PartnerProductsCtrl as vm'
        }
      },
      label: 'Partner Products',
      access: 'editNetwork',
      resolve: {
        enabled: /* @ngInject */ function ($state, authSvc) {
          if (!authSvc.hasEnabledFlag('FEATURE_PARTNERS_ENABLED')) {
            $state.go('app.404')
          }
        }
      }
    })
    /**
     * abstract state for /discount-campaigns
     */
    .state('app.organizations.organization.discountCampaigns', {
      abstract: true,
      url: '/discount-campaigns',
      template: '<ui-view/>'
    })

    /**
     * controller {@link mwApp/packages.controller:DiscountCampaignsCtrl}
     * @name /organizations/:packageId/discount-campaigns
     * @memberof mwApp/packages.config:ConfigRoutes
     */
    .state('app.organizations.organization.discountCampaigns.list', {
      url: '',
      templateUrl: 'components/packages/views/discountCampaigns.html',
      controller: 'DiscountCampaignsCtrl as vm',
      label: 'Discount Campaigns',
      access: 'viewDiscountCampaigns',
      resolve: {}
    })
    /**
     * controller {@link mwApp/addDiscountCampaign.controller:AddDiscountCampaignCtrl}
     * @name /organizations/:packageId/discount-campaigns/:id
     * @memberof mwApp/packages.config:ConfigRoutes
     */
    .state('app.organizations.organization.discountCampaigns.discountCampaign', {
      url: '/:id',
      templateUrl: 'components/packages/views/addDiscountCampaign.html',
      controller: 'AddDiscountCampaignCtrl as vm',
      label: '{{::id | addOrEdit}} Discount Campaign',
      access: 'viewDiscountCampaigns',
      resolve: {
        dc: /* @ngInject */ function ($transition$, DiscountCampaign) {
          var { id, packageId: networkId } = $transition$.params()
          return +id
            ? DiscountCampaign.load({ id, networkId }).then((res) => {
              res.networkId = networkId
              return res
            })
            : new DiscountCampaign({ networkId })
        }
      }
      // access: ':id (+) ! IF createDiscountCampaign ELSE editDiscountCampaign'
    })

    /**
     * abstract state for /past-discounts
     */
    .state('app.organizations.organization.pastDiscounts', {
      abstract: true,
      url: '/past-discounts',
      template: '<ui-view/>'
    })
    /**
     * controller {@link mwApp/packages.controller:DiscountCampaignsCtrl}
     * @name /organizations/:packageId/discount-campaigns
     * @memberof mwApp/packages.config:ConfigRoutes
     */
    .state('app.organizations.organization.pastDiscounts.list', {
      url: '',
      templateUrl: 'components/packages/views/pastDiscounts.html',
      controller: 'DiscountCampaignsCtrl as vm',
      label: 'Past Discounts',
      access: 'viewDiscountCampaigns',
      resolve: {}
    })

    /**
     * controller {@link mwApp/addDiscountCampaign.controller:ViewPastDiscountCampaignCtrl}
     * @name /organizations/:packageId/past-discounts/:id
     * @memberof mwApp/packages.config:ConfigRoutes
     */
    .state('app.organizations.organization.pastDiscounts.pastDiscount', {
      url: '/:id',
      templateUrl: 'components/packages/views/viewPastDiscountCampaign.html',
      controller: 'ViewPastDiscountCampaignCtrl as vm',
      label: 'View Past Discount Campaign',
      access: 'viewDiscountCampaigns',
      resolve: {
        dc: /* @ngInject */ function ($transition$, DiscountCampaign) {
          var { id, packageId: networkId } = $transition$.params()
          return +id
            ? DiscountCampaign.load({ id, networkId }).then((res) => new DiscountCampaign({ ...res, networkId }))
            : new DiscountCampaign({ networkId })
        }
      }
      // access: ':id (+) ! IF createDiscountCampaign ELSE editDiscountCampaign'
    })
}
